import type { Location } from '@/lib/services/locations/locations.types'
import type { HexColor, TerraDraw, TerraDrawExtend } from 'terra-draw'
import { Typography } from '@/components/ui/typography'
import { useGoogleMaps } from '@/hooks/use-google-maps'
import { PolygonType } from '@/lib/enums/PolygonType'
import { useFormContext } from '@/lib/form-contexts'
import { locationFilters, useLocations } from '@/lib/services/locations/locations.query'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import { useStore } from '@tanstack/react-form'
import { isRecord } from '@/lib/utils'
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { toast } from 'sonner'
import { MapControls } from './components/MapControls'
import { PolygonList } from './components/PolygonList'
import { BASE_DELIVERY_AREA_COLOR, DEFAULT_DELIVERY_AREA_FILL_OPACITY } from './constants'
import { useMapSetup, type DrawnPolygonFeature } from './hooks/useMapSetup'
import { useOtherLocationsPolygons } from './hooks/useOtherLocationsPolygons'
import { usePolygonLoading } from './hooks/usePolygonLoading'
import {
  encodePolygonFromGeoJson,
  focusMapOnGeoJsonFeature,
  getMapsUtilities,
  getTerraDrawFeatureById,
  getTopSelectableTerraDrawFeatureAtMapClick,
  selectTerraDrawFeature,
  type MapPolygonEntry,
} from './utils'

const EMPTY_LIST: never[] = []

/** A delivery area in the form's `delivery_area_fees`, which the form context doesn't type. */
interface FormPolygon {
  reference_id?: unknown
  encoded_polygon?: unknown
  name?: unknown
  color?: unknown
  polygon_type?: unknown
}

const isFormPolygonList = (value: unknown): value is FormPolygon[] =>
  Array.isArray(value) && value.every((polygon: unknown) => isRecord(polygon))

interface DeliveryAreaMapProps {
  location?: Location | null
  showPolygonList?: boolean | 'others-only'
  hiddenPolygonReferenceIds?: string[]
  onPolygonComplete?: ((feature: DrawnPolygonFeature, encodedPolygon: string) => void) | null
  selectedPolygonIndex?: number | null
  focusRequestId?: number
  onPolygonSelect?: ((index: number | null) => void) | null
  /** A polygon type to draw, null to stop drawing, or undefined to leave drawing uncontrolled. */
  drawingMode?: string | null
  drawingColor?: HexColor | null
  drawingFillOpacity?: number
  onStopDrawing?: (() => void) | null
  terraDrawRef?: RefObject<TerraDraw | null> | null
  isPointTestMode?: boolean
  onPointTest?: ((point: google.maps.LatLngLiteral) => void) | null
  pointTestMarker?: google.maps.LatLngLiteral | null
  mapHeightClassName?: string
}

const DeliveryAreaMap = ({
  location = null, // Location data passed from parent
  showPolygonList = true,
  hiddenPolygonReferenceIds = EMPTY_LIST, // Array of reference_ids to hide from the map
  // Props for DeliveryAreaBasedFees integration
  onPolygonComplete: externalOnPolygonComplete = null, // External handler for polygon creation
  selectedPolygonIndex: externalSelectedIndex = undefined, // Controlled selection from parent
  focusRequestId = 0,
  onPolygonSelect: externalOnPolygonSelect = null, // Callback when polygon selected on map
  drawingMode: externalDrawingMode = undefined, // Controlled drawing mode from parent
  drawingColor: externalDrawingColor = null, // Color to use when drawing (for immediate visual feedback)
  drawingFillOpacity: externalDrawingFillOpacity = DEFAULT_DELIVERY_AREA_FILL_OPACITY,
  onStopDrawing: externalOnStopDrawing = null, // Callback when drawing should stop
  terraDrawRef: externalTerraDrawRef = null, // Exposed ref for parent control
  isPointTestMode = false,
  onPointTest = null,
  pointTestMarker = null,
  mapHeightClassName = 'h-[min(68vh,48rem)] min-h-[34rem]',
}: DeliveryAreaMapProps) => {
  const form = useFormContext()

  const filters = locationFilters().get()
  const { data: allLocations = [] } = useLocations({ filters })

  // Derive from the location prop
  const initialPolygons = location?.delivery_area_fees ?? EMPTY_LIST
  const locationAddress = location?.address || null

  const otherLocationsPolygons = useMemo(
    () =>
      allLocations
        .filter((loc) => loc.id !== location?.id && (loc.delivery_area_fees?.length ?? 0) > 0)
        .flatMap((loc) =>
          (loc.delivery_area_fees ?? [])
            .filter(
              (area) => area.is_active !== false && area.polygon_type === PolygonType.Base.Value
            )
            .map((area) => ({
              ...area,
              locationId: loc.id,
              locationName: getLocationName(loc),
            }))
        ),
    [allLocations, location?.id]
  )

  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const polygonsRef = useRef<MapPolygonEntry[]>([])
  const otherLocationsPolygonsRef = useRef<google.maps.Polygon[]>([]) // Store read-only polygons from other locations
  const internalTerraDrawRef = useRef<TerraDraw | null>(null)
  const terraDrawRef = externalTerraDrawRef || internalTerraDrawRef
  const locationMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const pointTestMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const lastHandledFocusRequestRef = useRef(focusRequestId)
  const [internalIsDrawing, setInternalIsDrawing] = useState(false)
  const [internalSelectedPolygonIndex, setInternalSelectedPolygonIndex] = useState<number | null>(
    null
  )
  const [editingPolygonIndex, setEditingPolygonIndex] = useState<number | null>(null)
  const [mapType, setMapType] = useState('roadmap')
  const [isTerraDrawReady, setIsTerraDrawReady] = useState(false)
  const FIELD_NAME = 'delivery_area_fees'

  // Use controlled values if provided, otherwise use internal state
  const isDrawing = externalDrawingMode !== undefined ? !!externalDrawingMode : internalIsDrawing
  const shouldUseCrosshairCursor = isPointTestMode || isDrawing

  const setIsDrawing = useMemo<(isDrawing: boolean) => void>(
    () => (externalDrawingMode !== undefined ? () => {} : setInternalIsDrawing),
    [externalDrawingMode]
  )
  const selectedPolygonIndex =
    externalSelectedIndex !== undefined ? externalSelectedIndex : internalSelectedPolygonIndex
  const setSelectedPolygonIndex: (index: number | null) => void =
    externalSelectedIndex !== undefined
      ? (index) => externalOnPolygonSelect?.(index)
      : setInternalSelectedPolygonIndex

  const { maps, geometry, marker, isLoaded, error } = useGoogleMaps(['maps', 'geometry', 'marker'])

  // Filter initial polygons to match current fee method and exclude hidden polygons
  const hiddenPolygonReferenceIdSet = useMemo(
    () => new Set<unknown>(hiddenPolygonReferenceIds),
    [hiddenPolygonReferenceIds]
  )

  const filteredInitialPolygons = useMemo(
    () =>
      initialPolygons.filter((p) => {
        const isNotHidden = !hiddenPolygonReferenceIdSet.has(p.reference_id)
        return isNotHidden
      }),
    [hiddenPolygonReferenceIdSet, initialPolygons]
  )

  const handlePolygonComplete = (geoJsonFeature: DrawnPolygonFeature) => {
    if (!geometry || !maps) {
      console.error('[handlePolygonComplete] Missing geometry or maps')
      return
    }

    // Convert GeoJSON to encoded polyline
    const encoded = encodePolygonFromGeoJson(geoJsonFeature.geometry, geometry, maps)
    if (!encoded) {
      console.error('[handlePolygonComplete] Failed to encode polygon')
      toast.error('Error encoding polygon')
      return
    }

    if (
      terraDrawRef.current &&
      typeof terraDrawRef.current.updateFeatureProperties === 'function' &&
      geoJsonFeature?.id
    ) {
      const immediateColor =
        externalDrawingColor || geoJsonFeature?.properties?.fillColor || BASE_DELIVERY_AREA_COLOR

      try {
        terraDrawRef.current.updateFeatureProperties(geoJsonFeature.id, {
          fillColor: immediateColor,
          fillOpacity: geoJsonFeature?.properties?.fillOpacity ?? externalDrawingFillOpacity,
          outlineColor: immediateColor,
          outlineWidth: 2,
          referenceId: geoJsonFeature.id,
        })
      } catch (err) {
        console.warn('[handlePolygonComplete] Failed to apply immediate styling', err)
      }
    }

    if (!externalOnPolygonComplete) {
      return
    }

    externalOnPolygonComplete(geoJsonFeature, encoded)
    if (terraDrawRef.current) {
      terraDrawRef.current.setMode('select')
      externalOnStopDrawing?.()
    }
  }

  // Setup the map and related components
  useMapSetup({
    isLoaded,
    maps,
    geometry,
    marker,
    mapRef,
    mapInstanceRef,
    locationMarkerRef,
    terraDrawRef,
    locationAddress,
    initialPolygons: filteredInitialPolygons,
    handlePolygonComplete,
    drawingColor: externalDrawingColor,
    drawingFillOpacity: externalDrawingFillOpacity,
    onTerraDrawReady: () => setIsTerraDrawReady(true),
  })

  // Handle map type change
  useEffect(() => {
    if (!mapInstanceRef.current || !maps) return

    const utils = getMapsUtilities(maps)
    const { MapTypeId } = utils
    if (!MapTypeId) return

    const mapTypeId =
      Object.entries(MapTypeId).find(([key]) => key === mapType.toUpperCase())?.[1] || mapType
    mapInstanceRef.current.setMapTypeId(mapTypeId)
  }, [mapType, maps])

  useEffect(() => {
    if (!isLoaded || !mapInstanceRef.current?.setOptions) {
      return
    }

    mapInstanceRef.current.setOptions({
      draggableCursor: shouldUseCrosshairCursor ? 'crosshair' : 'grab',
      draggingCursor: shouldUseCrosshairCursor ? 'crosshair' : 'grabbing',
    })
  }, [isLoaded, shouldUseCrosshairCursor])

  // Watch form field value to react to resets
  const storedPolygonsValue: unknown = useStore(
    form.store,
    (state) => state.values[FIELD_NAME] || []
  )
  const currentPolygonsValue = isFormPolygonList(storedPolygonsValue)
    ? storedPolygonsValue
    : EMPTY_LIST

  const filteredCurrentPolygons = useMemo(
    () =>
      currentPolygonsValue.filter((p) => {
        const isNotHidden = !hiddenPolygonReferenceIdSet.has(p.reference_id)
        return isNotHidden
      }),
    [currentPolygonsValue, hiddenPolygonReferenceIdSet]
  )

  // Start drawing when external drawing mode changes
  useEffect(() => {
    // Skip if Terra Draw isn't ready yet
    if (!terraDrawRef.current || !isTerraDrawReady) {
      return
    }

    if (externalDrawingMode) {
      // Switch to polygon drawing mode
      terraDrawRef.current.setMode('polygon')
    } else if (externalDrawingMode === null) {
      // Explicitly set to null means stop drawing
      terraDrawRef.current.setMode('select')
    }
    // Note: undefined means uncontrolled, don't change mode
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terraDrawRef is a stable ref
  }, [externalDrawingMode, isTerraDrawReady])

  const stopDrawing = useCallback(() => {
    if (!terraDrawRef.current) {
      return
    }

    terraDrawRef.current.setMode('select')
    setIsDrawing(false)
  }, [setIsDrawing, terraDrawRef])

  // Reset UI state when form is reset (polygons change back to initial state)
  // This handles both cases: reset to empty (new polygon removed) and reset to initial (changes undone)
  // Use a ref to track previous value to only react to actual resets, not all changes
  const prevPolygonsValueRef = useRef(currentPolygonsValue)
  useEffect(() => {
    const prevLength = prevPolygonsValueRef.current?.length || 0
    const currentLength = currentPolygonsValue?.length || 0

    // Only react if polygons were actually removed (reset scenario)
    // Don't interfere if polygons are being added (drawing scenario) or if count stayed the same (save scenario)
    if (currentLength < prevLength) {
      // Close edit dialog if the polygon being edited no longer exists or was reset
      if (editingPolygonIndex !== null) {
        if (
          editingPolygonIndex >= currentPolygonsValue.length ||
          !currentPolygonsValue[editingPolygonIndex]
        ) {
          setEditingPolygonIndex(null)
        }
      }
      // Deselect polygon if it no longer exists
      if (
        selectedPolygonIndex !== null &&
        (selectedPolygonIndex >= currentPolygonsValue.length ||
          !currentPolygonsValue[selectedPolygonIndex])
      ) {
        setSelectedPolygonIndex(null)
      }
      // Stop drawing if polygons were reset to empty
      if (isDrawing && currentPolygonsValue.length === 0) {
        stopDrawing()
      }
    } else if (currentLength === prevLength && currentLength > 0) {
      // Same number of polygons - check if the polygon being edited actually changed (save operation)
      // Only close dialog if the edited polygon's data changed, not just because editingPolygonIndex changed
      if (editingPolygonIndex !== null && editingPolygonIndex < currentPolygonsValue.length) {
        const prevPolygon = prevPolygonsValueRef.current?.[editingPolygonIndex]
        const currentPolygon = currentPolygonsValue[editingPolygonIndex]

        if (prevPolygon && currentPolygon) {
          // Check if the edited polygon's data actually changed (save operation)
          const dataChanged =
            prevPolygon.encoded_polygon !== currentPolygon.encoded_polygon ||
            prevPolygon.name !== currentPolygon.name ||
            prevPolygon.color !== currentPolygon.color ||
            prevPolygon.polygon_type !== currentPolygon.polygon_type

          if (dataChanged) {
            // Polygon data changed (save operation) - close the dialog
            setEditingPolygonIndex(null)
          }
        }
      }

      // Check if the polygon at the selected index still exists
      if (selectedPolygonIndex !== null && selectedPolygonIndex < currentPolygonsValue.length) {
        const currentPolygon = currentPolygonsValue[selectedPolygonIndex]
        // Only deselect if the polygon was actually removed/replaced
        if (!currentPolygon) {
          setSelectedPolygonIndex(null)
        }
      }
    }

    // Update ref for next comparison
    prevPolygonsValueRef.current = currentPolygonsValue
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSelectedPolygonIndex is a stable setter
  }, [currentPolygonsValue, selectedPolygonIndex, isDrawing, stopDrawing, editingPolygonIndex])

  usePolygonLoading({
    isLoaded,
    maps,
    geometry,
    mapInstanceRef,
    polygonsRef,
    terraDrawRef,
    initialPolygons: filteredInitialPolygons,
    currentPolygonsValue: filteredCurrentPolygons,
    isTerraDrawReady,
    selectedPolygonReferenceId:
      externalSelectedIndex === undefined
        ? undefined
        : selectedPolygonIndex === null
          ? null
          : currentPolygonsValue[selectedPolygonIndex]?.reference_id,
  })

  // Load read-only polygons from other locations
  useOtherLocationsPolygons({
    isLoaded,
    maps,
    geometry,
    mapInstanceRef,
    otherLocationsPolygonsRef,
    otherLocationsPolygons,
  })

  useEffect(() => {
    if (pointTestMarker || !pointTestMarkerRef.current) {
      return
    }

    pointTestMarkerRef.current.map = null
    pointTestMarkerRef.current = null
  }, [pointTestMarker])

  useEffect(() => {
    if (!pointTestMarker || !isLoaded || !maps || !marker || !mapInstanceRef.current) {
      return
    }

    const lat = Number(pointTestMarker.lat)
    const lng = Number(pointTestMarker.lng)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }

    const utils = getMapsUtilities(maps, marker)
    const { AdvancedMarkerElement, PinElement, LatLng } = utils
    if (!AdvancedMarkerElement) {
      return
    }

    const position = LatLng ? new LatLng(lat, lng) : { lat, lng }

    if (!pointTestMarkerRef.current) {
      const pinElement = PinElement
        ? new PinElement({
            background: '#0ea5e9',
            borderColor: '#ffffff',
            glyphColor: '#ffffff',
            scale: 0.9,
          })
        : null

      pointTestMarkerRef.current = new AdvancedMarkerElement({
        content: pinElement || null,
        map: mapInstanceRef.current,
        position,
        title: 'Delivery test point',
      })
    } else {
      pointTestMarkerRef.current.position = position
      pointTestMarkerRef.current.map = mapInstanceRef.current
    }
  }, [isLoaded, maps, marker, pointTestMarker])

  useEffect(() => {
    return () => {
      if (pointTestMarkerRef.current) {
        pointTestMarkerRef.current.map = null
        pointTestMarkerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isLoaded || !maps || !mapInstanceRef.current || !isPointTestMode || !onPointTest) {
      return
    }

    const utils = getMapsUtilities(maps)
    const map = mapInstanceRef.current
    let lastClickKey: string | null = null

    const handleMapClick = (mapEvent: google.maps.MapMouseEvent) => {
      const latLng = mapEvent?.latLng
      const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat
      const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng

      if (
        typeof lat !== 'number' ||
        typeof lng !== 'number' ||
        !Number.isFinite(lat) ||
        !Number.isFinite(lng)
      ) {
        return
      }

      const clickKey = `${lat.toFixed(6)},${lng.toFixed(6)}`
      if (clickKey === lastClickKey) {
        return
      }
      lastClickKey = clickKey

      onPointTest({ lat, lng })
    }

    const listeners: google.maps.MapsEventListener[] = []
    if (utils.event?.addListener) {
      listeners.push(utils.event.addListener(map, 'click', handleMapClick))
    } else if (typeof map.addListener === 'function') {
      listeners.push(map.addListener('click', handleMapClick))
    }

    const dataListener = map.data?.addListener?.('click', handleMapClick)
    if (dataListener) {
      listeners.push(dataListener)
    }

    return () => {
      listeners.forEach((listener) => listener?.remove?.())
    }
  }, [isLoaded, isPointTestMode, maps, onPointTest])

  // Select polygon in Terra Draw when external selectedPolygonIndex changes
  useEffect(() => {
    if (
      externalSelectedIndex === undefined ||
      externalSelectedIndex === null ||
      !terraDrawRef.current ||
      !isTerraDrawReady
    ) {
      return
    }

    // Get the polygon data from the FULL form array using the global index
    const storedPolygons: unknown = form.state.values[FIELD_NAME] || []
    const allPolygons = isFormPolygonList(storedPolygons) ? storedPolygons : EMPTY_LIST
    const selectedPolygonData = allPolygons[externalSelectedIndex]

    if (!selectedPolygonData?.reference_id) {
      return
    }

    // Find the Terra Draw feature by reference_id (works regardless of filtering)
    const mockPolygon = polygonsRef.current.find(
      (p) => p.polygonData?.reference_id === selectedPolygonData.reference_id
    )
    const terraDrawId = mockPolygon?.terraDrawId

    if (terraDrawId) {
      // Ensure we're in select mode
      if (terraDrawRef.current.getMode() !== 'select') {
        terraDrawRef.current.setMode('select')
      }

      let retryFrameId: number | null = null
      const focusAndSelect = (hasRetried = false) => {
        if (!terraDrawRef.current) {
          return
        }

        const feature = getTerraDrawFeatureById(terraDrawRef.current, terraDrawId)
        if (!feature) {
          if (!hasRetried) {
            retryFrameId = requestAnimationFrame(() => focusAndSelect(true))
          }
          return
        }

        if (focusRequestId !== lastHandledFocusRequestRef.current) {
          const didFocus = focusMapOnGeoJsonFeature({
            feature,
            mapInstance: mapInstanceRef.current,
            maps,
            maxZoom: 18,
            padding: 96,
          })

          if (didFocus) {
            lastHandledFocusRequestRef.current = focusRequestId
          }
        }

        selectTerraDrawFeature(terraDrawRef.current, terraDrawId)
      }

      const frameId = requestAnimationFrame(() => {
        focusAndSelect()
      })

      return () => {
        window.cancelAnimationFrame?.(frameId)
        if (retryFrameId) {
          window.cancelAnimationFrame?.(retryFrameId)
        }
      }
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terraDrawRef is a stable ref
  }, [externalSelectedIndex, isTerraDrawReady, form, maps, currentPolygonsValue, focusRequestId])

  // Listen for polygon selection on the map (when user clicks a polygon)
  // to notify parent component
  useEffect(() => {
    if (!terraDrawRef.current || !isTerraDrawReady || !externalOnPolygonSelect || isPointTestMode) {
      return
    }

    const handleSelect = (selectedId: TerraDrawExtend.FeatureId) => {
      // Ignore selection events when in drawing mode (polygon mode)
      // This prevents accidental selection of existing polygons while drawing
      const currentMode = terraDrawRef.current?.getMode()
      if (currentMode === 'polygon') {
        return
      }

      // Find which polygon this feature corresponds to
      const mockPolygon = polygonsRef.current.find((p) => p.terraDrawId === selectedId)
      const referenceId = mockPolygon?.polygonData?.reference_id

      if (referenceId) {
        // Find the global index in the FULL form array by reference_id
        const storedPolygons: unknown = form.state.values[FIELD_NAME] || []
        const allPolygons = isFormPolygonList(storedPolygons) ? storedPolygons : EMPTY_LIST
        const globalIndex = allPolygons.findIndex((p) => p.reference_id === referenceId)
        if (globalIndex !== -1) {
          externalOnPolygonSelect(globalIndex)
        }
      }
    }

    const handleDeselect = () => {
      // Ignore deselection events when in drawing mode
      const currentMode = terraDrawRef.current?.getMode()
      if (currentMode === 'polygon') {
        return
      }
      // When a polygon is deselected, notify parent
      externalOnPolygonSelect(null)
    }

    const terraDraw = terraDrawRef.current
    terraDraw.on('select', handleSelect)
    terraDraw.on('deselect', handleDeselect)

    return () => {
      if (terraDraw) {
        terraDraw.off('select', handleSelect)
        terraDraw.off('deselect', handleDeselect)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerraDrawReady, externalOnPolygonSelect, isPointTestMode])

  // Terra Draw's select mode can miss fill clicks on small polygons after a rule is deselected.
  // Google Maps data clicks still identify the polygon, so use that as a selection-only fallback.
  useEffect(() => {
    if (!terraDrawRef.current || !isTerraDrawReady || !externalOnPolygonSelect || isPointTestMode) {
      return
    }

    const dataLayer = mapInstanceRef.current?.data
    if (typeof dataLayer?.addListener !== 'function') {
      return
    }

    let clickFrameId: number | null = null

    const handleDataFeatureClick = (mapEvent: google.maps.Data.MouseEvent) => {
      const terraDraw = terraDrawRef.current
      if (!terraDraw || terraDraw.getMode?.() === 'polygon') {
        return
      }

      if (clickFrameId) {
        window.cancelAnimationFrame?.(clickFrameId)
      }

      clickFrameId = requestAnimationFrame(() => {
        clickFrameId = null

        const activeTerraDraw = terraDrawRef.current
        if (!activeTerraDraw || activeTerraDraw.getMode?.() === 'polygon') {
          return
        }

        const selectedFeature = activeTerraDraw
          .getSnapshot?.()
          ?.find((feature) => feature.properties?.selected === true)
        const selectedPolygon = polygonsRef.current.find(
          (polygon) => String(polygon.terraDrawId) === String(selectedFeature?.id)
        )

        if (selectedPolygon) {
          return
        }

        const clickedFeature = getTopSelectableTerraDrawFeatureAtMapClick({
          terraDraw: activeTerraDraw,
          mapEvent,
          polygons: polygonsRef.current,
        })

        if (!clickedFeature) {
          return
        }

        const mockPolygon = polygonsRef.current.find(
          (polygon) => String(polygon.terraDrawId) === String(clickedFeature.id)
        )
        const referenceId = mockPolygon?.polygonData?.reference_id
        if (!referenceId) {
          return
        }

        const storedPolygons: unknown = form.state.values[FIELD_NAME] || []
        const allPolygons = isFormPolygonList(storedPolygons) ? storedPolygons : EMPTY_LIST
        const globalIndex = allPolygons.findIndex((polygon) => polygon.reference_id === referenceId)
        if (globalIndex === -1) {
          return
        }

        selectTerraDrawFeature(activeTerraDraw, clickedFeature.id)
        externalOnPolygonSelect(globalIndex)
      })
    }

    const listener = dataLayer.addListener('click', handleDataFeatureClick)

    return () => {
      if (clickFrameId) {
        window.cancelAnimationFrame?.(clickFrameId)
      }
      listener?.remove?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terraDrawRef and mapInstanceRef are stable refs
  }, [isTerraDrawReady, externalOnPolygonSelect, form, isPointTestMode])

  if (error) {
    return (
      <div className="p-4">
        <Typography variant="supporting" tone="destructive">
          Error loading Google Maps:{' '}
          {isRecord(error) && typeof error.message === 'string' ? error.message : null}
        </Typography>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className={`relative w-full rounded-lg border border-border ${mapHeightClassName}`}>
        <div
          ref={mapRef}
          className={`h-full w-full rounded-lg ${shouldUseCrosshairCursor ? 'cursor-crosshair' : ''}`}
        />
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-lg">
            <Typography variant="supporting">Loading map...</Typography>
          </div>
        )}
        {isLoaded && <MapControls mapType={mapType} onMapTypeChange={setMapType} />}
      </div>

      {showPolygonList && <PolygonList otherLocationsPolygons={otherLocationsPolygons} />}
    </div>
  )
}

export default DeliveryAreaMap
